/**
 * OrbitPrint — the plan picture for the tenant UI (slice 4).
 *
 * One fetch of GET /usage shared by the banner, the usage badge, the location cards, the print
 * dialog, the members page and the plans page. Re-fetched when the org changes, when the server
 * says "locations changed" (a plan change emits that), and on demand after an action.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { orbitApi, useOrbitAuth } from './OrbitAuthContext';
import { useLiveRefresh } from './live';

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const { org, user } = useOrbitAuth();
  const [plan, setPlan] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) { setPlan(null); return; }
    try { const r = await orbitApi.get('/usage'); setPlan(r.data); } catch { /* the badge simply stays quiet */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh, org?.id]);
  useLiveRefresh('locations', refresh);

  const dismissNotice = useCallback(async (key) => {
    try { await orbitApi.post('/usage/dismiss-notice', { key }); } catch { /* a failed dismiss just shows again next load */ }
    setPlan((p) => (p ? { ...p, notice: null } : p));
  }, []);

  const value = useMemo(() => ({ plan, refresh, dismissNotice }), [plan, refresh, dismissNotice]);
  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan() {
  const ctx = useContext(PlanContext);
  return ctx || { plan: null, refresh: () => {}, dismissNotice: () => {} };
}

export const PLAN_LABEL = { free: 'Free', premium: 'Premium', enterprise: 'Enterprise', founder: 'Founder' };

export function fmtDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? iso.slice(0, 10) : d.toLocaleDateString('en-PH', { month: 'long', day: 'numeric' });
}

/** "N of 30 documents left this month" — null when the plan is unlimited. */
export function docsLeft(plan) {
  if (!plan || plan.limit == null) return null;
  return Math.max(0, plan.limit - (plan.documents || 0));
}
